import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const TracksSection = ({ tracks }) => {
  const { t } = useTranslation('conferences');

  // Max columns depends on how many tracks there are (avoid empty columns).
  const maxCols = tracks?.length === 1 ? 1 : tracks?.length === 2 ? 2 : 3;

  const gridColsClass =
    maxCols === 1
      ? 'grid-cols-1'
      : maxCols === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  // Long descriptions get WIDER (span more columns) instead of getting tall.
  // Dense flow + col-span means a wide card that doesn't fit wraps to the next
  // row and the remaining cards backfill the gaps. (Literal class strings so
  // Tailwind's JIT keeps them.)
  const getSpanClass = (track) => {
    const len = track?.description?.length || 0;
    if (maxCols >= 3 && len > 320) return 'md:col-span-2 lg:col-span-3';
    if (maxCols >= 2 && len > 160) return 'md:col-span-2';
    return '';
  };

  return (
    <section className="py-4 md:py-6 bg-muted/30 rounded-lg">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-3 mb-4 md:mb-5">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Layers" size={20} className="text-accent" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">{t('conferenceLanding.conferenceTracks')}</h2>
        </div>

        <div className={`grid ${gridColsClass} grid-flow-row-dense items-start gap-4 md:gap-6`}>
          {tracks?.map((track) => (
            <div
              key={track?.id}
              className={`${getSpanClass(track)} bg-card border border-border rounded-lg shadow-elevation-1 p-4 md:p-6 hover:shadow-elevation-3 transition-all duration-250`}
            >
              <div className="flex items-start gap-3 mb-3 md:mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={track?.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">{track?.name}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground caption">{track?.chair}</p>
                </div>
              </div>

              <p className="text-sm md:text-base text-foreground leading-relaxed mb-4">
                {track?.description}
              </p>

              <div className="pt-4 border-t border-border">
                <div className="flex flex-wrap gap-1.5">
                  {track?.keywords?.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex px-2 py-1 rounded-md bg-muted text-xs text-foreground caption"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TracksSection;